import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';

import { type WorkflowCatalogEntry } from '@/features/system/orchestrator/api/workflowsApi';
import { fetchWorkflowsV2Api, triggerWorkflowV2Api, type StartWorkflowResponse, type TriggerWorkflowV2Input } from '@/features/system/orchestrator/api/workflowsV2Api';
import { coerceObject, computeTreeLayoutVertical, formatJson, fromDag } from '@/features/system/orchestrator/components/workflowBuilder/layout';
import WorkflowGraphView from '@/features/system/orchestrator/components/workflowBuilder/WorkflowGraphView';
import { useWorkflowRunStream } from '@/features/system/orchestrator/hooks/useWorkflowRunStream';
import { useJobProgressStream } from '@/features/system/jobs/hooks/useJobProgressStream';
import WorkflowActivityLogPanel from '../monitoring/WorkflowActivityLogPanel';
import WorkflowAiConsumePanel from '../monitoring/WorkflowAiConsumePanel';
import WorkflowMediaAudioSplitPanel from '../media/WorkflowMediaAudioSplitPanel';
import WorkflowOutcomesPanel from '../outcomes/WorkflowOutcomesPanel';
import WorkflowScopesPanel from '../scopes/WorkflowScopesPanel';
import WorkflowStatsPanel from '../monitoring/WorkflowStatsPanel';
import Tabs from '@/shared/components/ui/Tabs';
import DarkDataTable from '@/shared/components/ui/DarkDataTable';
import { timeAgo } from '@/utils/date';
import { getStatusTextClass, isRunningStatus, normalizeRunStatus, toStatusLabel, vumeterTextClass } from '@/utils/runStatusUi';
import { useNow } from '@/utils/useNow';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import {
  type JsonSchema,
  type FieldSpec,
  extractDag,
  collectSchemaFields,
  inferFieldKind,
  buildInputsPayload,
  buildDefaultProjectName,
  safeStringify,
  FieldRow,
} from './WorkflowRunnerForm';

const QUERY_KEY = ['developer', 'workflowRunner', 'workflowCatalog'] as const;

export default function WorkflowRunnerPanel() {
  const subTabLabels = ['Workflows/Runner', 'Activity Log', 'Statistics', 'Outcomes', 'Scopes', 'AI CONSUME', 'media.audio.split'];
  const [activeSubTab, setActiveSubTab] = useState<string>('Workflows/Runner');
  const [sp] = useSearchParams();
  useDocumentTitle('Developer', 'Workflow', activeSubTab);

  useEffect(() => {
    if (!subTabLabels.includes(activeSubTab)) setActiveSubTab('Workflows/Runner');
  }, [activeSubTab, subTabLabels.join('|')]);

  // Sync with URL when BubbleNav navigates while already mounted
  useEffect(() => {
    const v = sp.get('runnerTab');
    if (v && subTabLabels.includes(v) && v !== activeSubTab) setActiveSubTab(v);
  }, [sp]);

  const { data: workflows = [], isLoading, error } = useQuery<WorkflowCatalogEntry[], Error>({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const res = await fetchWorkflowsV2Api({ limit: 500 });
      // v2 endpoint returns the same shape, but timestamps may be missing.
      const items = (res.items ?? []) as unknown as WorkflowCatalogEntry[];
      return items.filter((w) => w?.is_active === true);
    },
    refetchInterval: 30_000,
    refetchIntervalInBackground: true,
  });

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const closeTimerRef = useRef<number | null>(null);
  const [closingKey, setClosingKey] = useState<string | null>(null);

  const visibleKey = selectedKey ?? closingKey;
  const selected = useMemo(() => workflows.find((w) => keyOf(w) === visibleKey) ?? null, [workflows, visibleKey]);

  const runnerCardRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current != null) window.clearTimeout(closeTimerRef.current);
    };
  }, []);

  const inputsSchema = useMemo<JsonSchema>(() => {
    if (!selected) return {};
    const dag = extractDag(selected.definition);
    const schema = (dag.inputs_schema ?? dag.inputs ?? {}) as unknown;
    return coerceObject(schema) as JsonSchema;
  }, [selected]);

  const fieldSpecs = useMemo<FieldSpec[]>(() => {
    const { properties, required } = collectSchemaFields(inputsSchema);
    return Object.keys(properties)
      .sort()
      .map((key) => {
        const raw = properties[key];
        const s = coerceObject(raw) as JsonSchema;
        const label = typeof s.title === 'string' && s.title.trim() ? s.title : key;
        const description = typeof s.description === 'string' ? s.description : undefined;
        const kind = inferFieldKind(s);

        return {
          key,
          label,
          description,
          required: required.has(key),
          kind,
          enumValues: Array.isArray(s.enum) ? s.enum : undefined,
          rawSchema: raw,
        };
      });
  }, [inputsSchema]);

  const [inputs, setInputs] = useState<Record<string, any>>({});
  const [jsonInputsText, setJsonInputsText] = useState<Record<string, string>>({});
  const [lastRun, setLastRun] = useState<StartWorkflowResponse | null>(null);
  const [runError, setRunError] = useState<string | null>(null);

  const runId = lastRun?.workflow_run_id ?? null;
  const {
    snapshot: runSnapshot,
    loading: runStreamLoading,
    error: runStreamError,
  } = useWorkflowRunStream(runId);

  const graph = useMemo(() => {
    if (!selected) return null;
    try {
      const parsed = fromDag(selected.definition);
      const inferredEdges = inferEdgesFromBindings(parsed.nodes, parsed.edges);
      const allEdges = [...parsed.edges, ...inferredEdges];
      const layout = computeTreeLayoutVertical(parsed.nodes, allEdges);

      const inSchema = coerceObject(parsed.inputsSchema);
      const inProps = coerceObject(inSchema.properties);
      const inputKeys = Object.keys(inProps).sort();
      const inputMetaByKey: Record<string, { type?: string; description?: string }> = {};
      for (const k of inputKeys) {
        const p = coerceObject(inProps[k]);
        inputMetaByKey[k] = {
          type: typeof p.type === 'string' ? p.type : undefined,
          description: typeof p.description === 'string' ? p.description : undefined,
        };
      }

      const outSchema = coerceObject(parsed.outputsSchema);
      const outProps = coerceObject(outSchema.properties);
      const outputKeys = Object.keys(outProps).sort();
      const outputMetaByKey: Record<string, { type?: string; description?: string }> = {};
      for (const k of outputKeys) {
        const p = coerceObject(outProps[k]);
        outputMetaByKey[k] = {
          type: typeof p.type === 'string' ? p.type : undefined,
          description: typeof p.description === 'string' ? p.description : undefined,
        };
      }

      return {
        nodes: parsed.nodes,
        edges: allEdges,
        layout,
        inputKeys,
        inputMetaByKey,
        outputKeys,
        outputMetaByKey,
        outputsBindingsJson: formatJson(parsed.outputsBindings),
      };
    } catch {
      return null;
    }
  }, [selected]);

  const [selectedGraphStepId, setSelectedGraphStepId] = useState<string>('');
  useEffect(() => {
    const next = graph?.nodes?.[0]?.id ?? '';
    setSelectedGraphStepId(next);
  }, [graph?.nodes?.[0]?.id]);

  const statusByStepId = useMemo(() => {
    const map: Record<string, string> = {};
    if (!runSnapshot || !Array.isArray(runSnapshot.steps)) return map;
    for (const s of runSnapshot.steps) {
      const id = String((s as any).step_id ?? '');
      if (!id) continue;
      map[id] = String((s as any).status ?? 'PENDING');
    }
    return map;
  }, [runSnapshot]);

  const runtimeInputs = useMemo(() => {
    const r: any = (runSnapshot as any)?.run;
    return (r?.inputs ?? r?.Inputs ?? {}) as Record<string, any>;
  }, [runSnapshot]);

  const runtimeStepById = useMemo(() => {
    const map: Record<string, any> = {};
    const steps: any[] = Array.isArray((runSnapshot as any)?.steps) ? (runSnapshot as any).steps : [];
    for (const s of steps) {
      const id = String(s?.step_id ?? s?.StepID ?? '');
      if (!id) continue;
      map[id] = {
        status: s?.status ?? s?.Status,
        last_error: s?.last_error ?? s?.LastError,
        output: s?.output ?? s?.Output,
        job_id: s?.job_id ?? s?.jobId ?? s?.id_job ?? s?.JobID ?? s?.worker_job_id ?? s?.device_job_id,
        job_type: s?.job_type ?? s?.jobType ?? s?.JobType,
        attempts: (typeof s?.attempts === 'number' ? s.attempts : (typeof s?.Attempts === 'number' ? s.Attempts : null)),
        updated_at: s?.updated_at ?? s?.UpdatedAt,
      };
    }
    return map;
  }, [runSnapshot]);

  const { events: jobProgressEvents } = useJobProgressStream(runId, { fallbackPollMs: 3000, autoStopOnDone: false });

  const deriveStepIdFromJobEvent = (evt: any): string => {
    const direct = String(evt?.step_id ?? evt?.stepId ?? '').trim();
    if (direct) return direct;

    const jid = String(evt?.job_id ?? evt?.jobId ?? '').trim();
    if (!jid) return '';

    // Convention: "<runId>:<stepId>" (example: "530:split_youtube_audio_stems")
    const rid = String(runId ?? '').trim();
    if (!rid) return '';
    const prefix = `${rid}:`;
    if (jid.startsWith(prefix)) return jid.slice(prefix.length);

    // Generic fallback: split on first ':' and accept remainder if it looks like a step id.
    const idx = jid.indexOf(':');
    if (idx <= 0 || idx >= jid.length - 1) return '';
    const maybe = jid.slice(idx + 1).trim();
    if (!maybe) return '';
    // Step ids in this app are typically [a-zA-Z0-9_-]
    if (!/^[a-zA-Z0-9_-]+$/.test(maybe)) return '';
    return maybe;
  };

  const stepProgressById = useMemo(() => {
    const map: Record<string, { pct?: number | null; message?: string | null }> = {};

    const isRefreshNoise = (pct: number | null, msg: unknown): boolean => {
      const m = (typeof msg === 'string' ? msg : '').trim().toLowerCase();
      if (!m) return false;
      // Backend/UI may emit placeholder “refreshing…” progress updates; never let them clobber
      // the last meaningful progress line.
      if (!m.startsWith('refreshing')) return false;
      return pct == null || pct === 0;
    };

    for (const e of jobProgressEvents || []) {
      const anyE: any = e as any;
      const stepId = deriveStepIdFromJobEvent(anyE);
      if (!stepId) continue;

      const pct = (
        (typeof anyE.pct === 'number' ? anyE.pct : null)
        ?? (typeof anyE.percent === 'number' ? anyE.percent : null)
        ?? (typeof anyE.progress === 'number' ? Math.round(anyE.progress * 100) : null)
        ?? (typeof anyE.stage_progress === 'number' ? Math.round(anyE.stage_progress * 100) : null)
        ?? (typeof anyE.overall_progress === 'number' ? Math.round(anyE.overall_progress * 100) : null)
      );

      const msg = (
        (typeof anyE.message === 'string' ? anyE.message : null)
        ?? (typeof anyE.stage === 'string' ? anyE.stage : null)
        ?? (typeof anyE.phase_name === 'string' ? anyE.phase_name : null)
        ?? (typeof anyE.phase === 'string' ? anyE.phase : null)
      );

      if (isRefreshNoise(pct, msg)) continue;
      map[stepId] = { pct, message: (typeof msg === 'string' ? msg : null) };
    }

    // Fallback: some orchestrator snapshots include per-step progress but the jobs stream may be quiet.
    // Only fill in missing stepIds so jobProgressEvents remains the preferred live signal.
    const steps: any[] = Array.isArray((runSnapshot as any)?.steps) ? (runSnapshot as any).steps : [];
    for (const s of steps) {
      const stepId = String(s?.step_id ?? s?.StepID ?? '').trim();
      if (!stepId) continue;
      if (map[stepId]) continue;
      const p = s?.progress;
      if (!p) continue;

      const pct = (
        (typeof p.pct === 'number' ? p.pct : null)
        ?? (typeof p.percent === 'number' ? p.percent : null)
        ?? (typeof p.progress === 'number' ? Math.round(p.progress * 100) : null)
        ?? (typeof p.stage_progress === 'number' ? Math.round(p.stage_progress * 100) : null)
        ?? (typeof p.overall_progress === 'number' ? Math.round(p.overall_progress * 100) : null)
      );

      const msg = (
        (typeof p.message === 'string' ? p.message : null)
        ?? (typeof p.stage === 'string' ? p.stage : null)
        ?? (typeof p.phase_name === 'string' ? p.phase_name : null)
        ?? (typeof p.phase === 'string' ? p.phase : null)
      );

      if (isRefreshNoise(pct, msg)) continue;
      map[stepId] = { pct, message: (typeof msg === 'string' ? msg : null) };
    }
    return map;
  }, [jobProgressEvents, runSnapshot, runId]);

  const stepProgressLinesById = useMemo(() => {
    const map: Record<string, string[]> = {};

    const isRefreshNoiseLine = (line: string): boolean => {
      const l = String(line || '').trim().toLowerCase();
      // Same idea as stepProgressById: ignore placeholder refresh chatter.
      return Boolean(l) && (l === '0% refreshing' || l.startsWith('0% refreshing') || l.startsWith('refreshing'));
    };

    const pushLine = (stepId: string, line: string) => {
      const s = String(stepId || '').trim();
      const l = String(line || '').trim();
      if (!s || !l) return;

      if (isRefreshNoiseLine(l)) return;
      // UX requirement: keep a single mutable progress line per step.
      // Overwrite instead of accumulating a log.
      map[s] = [l];
    };

    for (const e of jobProgressEvents || []) {
      const anyE: any = e as any;
      const stepId = deriveStepIdFromJobEvent(anyE);
      if (!stepId) continue;

      const pct = (
        (typeof anyE.pct === 'number' ? anyE.pct : null)
        ?? (typeof anyE.percent === 'number' ? anyE.percent : null)
        ?? (typeof anyE.progress === 'number' ? Math.round(anyE.progress * 100) : null)
        ?? (typeof anyE.stage_progress === 'number' ? Math.round(anyE.stage_progress * 100) : null)
        ?? (typeof anyE.overall_progress === 'number' ? Math.round(anyE.overall_progress * 100) : null)
      );

      const msg = (
        (typeof anyE.message === 'string' ? anyE.message : null)
        ?? (typeof anyE.stage === 'string' ? anyE.stage : null)
        ?? (typeof anyE.phase_name === 'string' ? anyE.phase_name : null)
        ?? (typeof anyE.phase === 'string' ? anyE.phase : null)
      );

      const line = (() => {
        const m = typeof msg === 'string' ? msg.trim() : '';
        if (pct != null && m) return `${pct}% ${m}`;
        if (pct != null) return `${pct}%`;
        if (m) return m;
        return '';
      })();

      if (line) pushLine(stepId, line);
    }

    // Fallback: snapshot progress may include log-ish lines.
    const steps: any[] = Array.isArray((runSnapshot as any)?.steps) ? (runSnapshot as any).steps : [];
    for (const s of steps) {
      const stepId = String(s?.step_id ?? s?.StepID ?? '').trim();
      if (!stepId) continue;
      const p: any = s?.progress;
      if (!p) continue;
      const lines = (
        (Array.isArray(p.lines) ? p.lines : null)
        ?? (Array.isArray(p.log_lines) ? p.log_lines : null)
        ?? (Array.isArray(p.logs) ? p.logs : null)
      );
      if (!lines) continue;
      for (const raw of lines.slice(-8)) {
        pushLine(stepId, String(raw ?? '').trim());
      }
    }

    return map;
  }, [jobProgressEvents, runSnapshot, runId]);

  const stepProgressUpdatedAtMsById = useMemo(() => {
    const map: Record<string, number> = {};
    for (const e of jobProgressEvents || []) {
      const anyE: any = e as any;
      const stepId = deriveStepIdFromJobEvent(anyE);
      if (!stepId) continue;

      const raw = (
        anyE.ts ?? anyE.time ?? anyE.at
        ?? anyE.created_at ?? anyE.updated_at
        ?? anyE.createdAt ?? anyE.updatedAt
        ?? anyE.t_ms ?? anyE.t
      );

      let ms: number | null = null;
      if (typeof raw === 'number' && Number.isFinite(raw)) {
        // Heuristic: seconds vs milliseconds
        ms = raw > 1_000_000_000_000 ? raw : Math.round(raw * 1000);
      } else if (typeof raw === 'string') {
        ms = parseDateMs(raw);
      }

      // If the backend doesn't send timestamps, use arrival time as best-effort.
      if (ms == null) ms = Date.now();
      map[stepId] = ms;
    }
    return map;
  }, [jobProgressEvents, runId]);

  useEffect(() => {
    if (!selected) {
      setInputs({});
      setJsonInputsText({});
      setLastRun(null);
      setRunError(null);
      return;
    }

    const dag = extractDag(selected.definition);
    const settingsAny = coerceObject((dag as any)?.settings);
    const runIdempotencyAny = coerceObject((settingsAny as any)?.run_idempotency);
    const scopeKeyFields: string[] = Array.isArray((runIdempotencyAny as any)?.scope_key_fields)
      ? (runIdempotencyAny as any).scope_key_fields.map((x: any) => String(x ?? '').trim()).filter(Boolean)
      : [];
    const wantsDefaultProjectName = scopeKeyFields.includes('project_name');

    const defaults: Record<string, any> = {};
    const jsonDefaults: Record<string, string> = {};
    for (const f of fieldSpecs) {
      const s = coerceObject(f.rawSchema) as JsonSchema;
      if (s.default !== undefined) {
        defaults[f.key] = s.default;
        if (f.kind === 'json') {
          jsonDefaults[f.key] = safeStringify(s.default);
        }
      } else {
        if (f.kind === 'boolean') defaults[f.key] = undefined;
        if (f.kind === 'number') defaults[f.key] = '';
        if (f.kind === 'enum') defaults[f.key] = '';
        if (f.kind === 'string') defaults[f.key] = '';
        if (f.kind === 'json') {
          defaults[f.key] = undefined;
          jsonDefaults[f.key] = '';
        }
      }

      // Opinionated default: if project_name is chosen as a deterministic run scope field,
      // give it a safe, per-day default like "yymmdd_some_slug".
      if (wantsDefaultProjectName && f.key === 'project_name' && f.kind === 'string') {
        const existing = defaults[f.key];
        const isEmpty = existing === undefined || (typeof existing === 'string' && existing.trim() === '');
        if (isEmpty) {
          defaults[f.key] = buildDefaultProjectName(selected.workflow_key);
        }
      }
    }
    setInputs(defaults);
    setJsonInputsText(jsonDefaults);
    setLastRun(null);
    setRunError(null);
  }, [selectedKey, fieldSpecs.length]);

  const startMutation = useMutation<StartWorkflowResponse, Error, TriggerWorkflowV2Input>({
    mutationFn: (payload) => triggerWorkflowV2Api(payload),
    onSuccess: (res) => {
      setLastRun(res);
      setRunError(null);
    },
    onError: (e) => {
      setLastRun(null);
      setRunError(e.message || 'Failed to start workflow');
    },
  });

  const onRun = async () => {
    if (!selected) return;
    setRunError(null);
    setLastRun(null);

    try {
      const inputsPayload = buildInputsPayload(fieldSpecs, inputs, jsonInputsText);

      await startMutation.mutateAsync({
        workflow_key: selected.workflow_key,
        version: selected.version,
        inputs: inputsPayload,
      });
    } catch (e: any) {
      setRunError(typeof e?.message === 'string' ? e.message : 'Failed to build workflow payload');
    }
  };

  return (
    <div className="h-full min-h-0 flex flex-col gap-4">
      <div className="text-sm text-neutral-400">
        Select a published workflow and run it with runtime inputs.
      </div>

      <Tabs
        tabs={subTabLabels}
        activeTab={activeSubTab}
        onTabChange={setActiveSubTab}
        urlKey="runnerTab"
        urlParams={{ devTab: 'Workflow' }}
      />

      <div className="flex-1 min-h-0">
        {activeSubTab === 'Workflows/Runner' && (
          <div className="h-full min-h-0 grid grid-cols-2 gap-4">
            {/* Left: workflow selection + runner params */}
            <div className="min-h-0 flex flex-col gap-4">
              <div
                className={
                  'rounded-md border border-neutral-800 flex flex-col min-h-0 ' +
                  // Single combined panel (list + inline runner).
                  'flex-1'
                }
              >
                <div className="px-3 py-2 border-b border-neutral-800 flex items-center justify-between">
                  <div className="text-sm text-neutral-200">Workflows</div>
                  <div className="text-xs text-neutral-500">Active only</div>
                </div>

                {isLoading && <div className="p-3 text-sm text-neutral-400">Loading workflows…</div>}
                {error && <div className="p-3 text-sm text-rose-400">Failed to load workflows: {error.message}</div>}

                {!isLoading && !error && (
                  <div className="flex-1 min-h-0 overflow-y-auto">
                    <div className="p-3">
                      <DarkDataTable<WorkflowCatalogEntry>
                        columns={[
                          {
                            header: 'Workflow',
                            tdClassName: 'text-neutral-200',
                            render: (w) => (
                              <div className="truncate" title={w.workflow_key}>{w.workflow_key}</div>
                            ),
                          },
                          {
                            header: 'Version',
                            thClassName: 'whitespace-nowrap',
                            tdClassName: 'text-neutral-400 whitespace-nowrap',
                            render: (w) => `v${w.version}`,
                          },
                          {
                            header: 'Updated',
                            thClassName: 'whitespace-nowrap',
                            tdClassName: 'text-neutral-500 whitespace-nowrap',
                            render: (w) => {
                              const ts = w.updated_at;
                              if (!ts) return '—';
                              return <span title={formatDateShort(ts)}>{timeAgo(ts)}</span>;
                            },
                          },
                        ]}
                        rows={workflows
                          .slice()
                          .sort((a, b) => {
                            const ak = a.workflow_key.localeCompare(b.workflow_key);
                            if (ak !== 0) return ak;
                            return b.version - a.version;
                          })
                        }
                        emptyMessage="No active workflows published."
                        getRowKey={(w) => keyOf(w)}
                        onRowClick={(w) => {
                          const k = keyOf(w);
                          const active = k === selectedKey;

                          if (active) {
                            // Toggle: clicking the same row again collapses the inline Runner.
                            if (closeTimerRef.current != null) window.clearTimeout(closeTimerRef.current);
                            setClosingKey(k);
                            setSelectedKey(null);
                            closeTimerRef.current = window.setTimeout(() => {
                              setClosingKey((curr) => (curr === k ? null : curr));
                              closeTimerRef.current = null;
                            }, 180);
                            return;
                          }

                          if (closeTimerRef.current != null) window.clearTimeout(closeTimerRef.current);
                          setClosingKey(null);
                          setSelectedKey(k);
                          // Ensure the Runner view is visible after selecting a workflow.
                          setActiveSubTab('Workflows/Runner');
                        }}
                        isRowSelected={(w) => keyOf(w) === selectedKey}
                        isRowExpanded={(w) => {
                          const k = keyOf(w);
                          const active = k === selectedKey;
                          const closing = k === closingKey && selectedKey === null;
                          return active || closing;
                        }}
                        renderExpandedRow={(w) => {
                          const k = keyOf(w);
                          const active = k === selectedKey;
                          const closing = k === closingKey && selectedKey === null;
                          const showInlineRunner = active || closing;
                          const runnerOpen = active;

                          if (!showInlineRunner || !selected || keyOf(selected) !== k) return null;

                          return (
                            <div
                              ref={runnerCardRef}
                              className={
                                'border-t border-neutral-800 overflow-hidden ' +
                                'transition-[max-height,opacity] duration-200 ease-out ' +
                                (runnerOpen ? 'max-h-[900px] opacity-100' : 'max-h-0 opacity-0 pointer-events-none')
                              }
                            >
                              <div className="px-3 py-3">
                                <div className="text-sm text-neutral-200">Runner</div>

                                <div className="mt-2 text-sm text-neutral-300">
                                  <div className="font-medium text-neutral-100">
                                    {selected.workflow_key}{' '}
                                    <span className="text-neutral-500">v{selected.version}</span>
                                  </div>
                                  <div className="text-xs text-neutral-500">Fill inputs from the workflow’s JSON Schema.</div>
                                </div>

                                {fieldSpecs.length === 0 && (
                                  <div className="mt-3 text-sm text-neutral-500">This workflow has no declared inputs.</div>
                                )}

                                <div className="mt-3 space-y-3">
                                  {fieldSpecs.map((f) => (
                                    <FieldRow
                                      key={f.key}
                                      spec={f}
                                      value={inputs[f.key]}
                                      jsonText={jsonInputsText[f.key] ?? ''}
                                      onChange={(next: any) => {
                                        setInputs((old) => ({ ...old, [f.key]: next }));
                                      }}
                                      onChangeJson={(next: string) => {
                                        setJsonInputsText((old) => ({ ...old, [f.key]: next }));
                                      }}
                                    />
                                  ))}
                                </div>

                                <div className="mt-4 flex items-center gap-3">
                                  <button
                                    type="button"
                                    disabled={startMutation.isPending}
                                    onClick={onRun}
                                    className="px-4 py-2 rounded-md bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50"
                                  >
                                    {startMutation.isPending ? 'Running…' : 'Run'}
                                  </button>

                                  {runError && <div className="text-sm text-rose-400">{runError}</div>}

                                  {lastRun && (
                                    <div className="text-sm text-emerald-300">
                                      Started: <span className="text-neutral-200">{lastRun.workflow_run_id}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right: graph viewer */}
            <div className="rounded-md border border-neutral-800 flex flex-col min-h-0">
              <div className="px-3 py-2 border-b border-neutral-800 flex items-center justify-between gap-3">
                <div className="text-sm text-neutral-200">Graph</div>
                {runId ? <div className="text-xs text-neutral-500 truncate">Run: {runId}</div> : null}
              </div>

              <div className="p-3 flex flex-col gap-3 flex-1 min-h-0">
                <div className="rounded-md border border-neutral-800 p-3">
                  <RunSummaryRich
                    runId={runId}
                    snapshot={runSnapshot}
                    loading={runStreamLoading}
                    error={runStreamError}
                  />
                </div>

                <div className="flex-1 min-h-0 rounded-md border border-neutral-800 overflow-hidden">
                  {graph && graph.nodes.length > 0 ? (
                    <WorkflowGraphView
                      nodes={graph.nodes}
                      edges={graph.edges}
                      layout={graph.layout}
                      orientation="vertical"
                      enableRunState={Boolean(runId)}
                      inputKeys={graph.inputKeys}
                      inputMetaByKey={graph.inputMetaByKey}
                      outputKeys={graph.outputKeys}
                      outputMetaByKey={graph.outputMetaByKey}
                      outputsBindingsJson={graph.outputsBindingsJson}
                      selectedStepId={selectedGraphStepId || graph.nodes[0]?.id || ''}
                      statusByStepId={statusByStepId}
                      runtimeInputs={runtimeInputs}
                      runtimeStepById={runtimeStepById}
                      stepProgressById={stepProgressById}
                      stepProgressUpdatedAtMsById={stepProgressUpdatedAtMsById}
                      stepProgressLinesById={stepProgressLinesById}
                      onSelect={setSelectedGraphStepId}
                    />
                  ) : (
                    <div className="h-full flex items-center justify-center">
                      <div className="text-sm text-neutral-500">Select a workflow to view its graph.</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeSubTab === 'Activity Log' && (
          <WorkflowActivityLogPanel />
        )}

        {activeSubTab === 'Statistics' && (
          <WorkflowStatsPanel />
        )}

        {activeSubTab === 'Outcomes' && (
          <WorkflowOutcomesPanel />
        )}

        {activeSubTab === 'Scopes' && (
          <WorkflowScopesPanel />
        )}

        {activeSubTab === 'AI CONSUME' && (
          <WorkflowAiConsumePanel />
        )}

        {activeSubTab === 'media.audio.split' && (
          <WorkflowMediaAudioSplitPanel />
        )}
      </div>
    </div>
  );
}

function formatMsCompact(ms: number | null | undefined): string {
  if (!Number.isFinite(ms as any)) return '—';
  const v = Math.max(0, Number(ms));
  if (v < 1000) return `${Math.round(v)}ms`;
  const s = v / 1000;
  if (s < 60) return `${s.toFixed(s < 10 ? 2 : 1)}s`;
  const m = Math.floor(s / 60);
  const rs = Math.round(s % 60);
  return `${m}m ${rs}s`;
}

function parseMaybeJson(v: any): any {
  if (typeof v !== 'string') return v;
  const text = v.trim();
  if (!text) return v;
  if ((text.startsWith('{') && text.endsWith('}')) || (text.startsWith('[') && text.endsWith(']'))) {
    try {
      return JSON.parse(text);
    } catch {
      return v;
    }
  }
  return v;
}

function coerceNumber(v: any): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const s = v.trim();
    if (!s) return null;
    const n = Number(s);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function parseDateMs(v: any): number | null {
  const s = typeof v === 'string' ? v.trim() : '';
  if (!s) return null;
  const d = new Date(s);
  const t = d.getTime();
  return Number.isFinite(t) ? t : null;
}

function extractDurationMs(output: any): number | null {
  if (output == null) return null;

  // Sometimes orchestrator returns output as JSON-stringified text.
  const out = parseMaybeJson(output);

  // Sometimes duration itself arrives as a numeric string.
  const directScalar = coerceNumber(out);
  if (directScalar != null) return directScalar;

  if (!out || typeof out !== 'object') return null;
  const o: any = out;

  const d0 = coerceNumber(o.duration_ms);
  if (d0 != null) return d0;

  if (o.meta && typeof o.meta === 'object') {
    const dm = coerceNumber((o.meta as any).duration_ms);
    if (dm != null) return dm;
  }

  if (o.result && typeof o.result === 'object') {
    const dr = coerceNumber((o.result as any).duration_ms);
    if (dr != null) return dr;
  }

  // Common wrapper: { output: {...} }
  if (o.output != null) return extractDurationMs(o.output);
  return null;
}

function deriveOverallStatusFromSnapshot(snapshot: any): string | null {
  if (!snapshot) return null;

  // If the backend explicitly marks the run as terminal, trust it.
  // This prevents transient FAILED flashes during retry/backoff windows.
  const declared = normalizeRunStatus(snapshot?.status);
  if (declared === 'FAILED' || declared === 'ERROR') return 'FAILED';
  if (declared === 'SUCCEEDED' || declared === 'DONE') return 'SUCCEEDED';
  if (isRunningStatus(declared)) return 'RUNNING';

  const counts = snapshot?.counts ?? null;
  const total = typeof counts?.total === 'number' ? counts.total : null;
  const succeeded = typeof counts?.succeeded === 'number' ? counts.succeeded : null;
  const failed = typeof counts?.failed === 'number' ? counts.failed : null;
  const running = typeof counts?.running === 'number' ? counts.running : null;
  const ready = typeof counts?.ready === 'number' ? counts.ready : null;
  const pending = typeof counts?.pending === 'number' ? counts.pending : null;

  // Prefer what the step graph is currently doing.
  // IMPORTANT: do NOT fail the whole run just because some attempts failed;
  // only fail once the run is terminal (i.e. all steps have finished).
  if (running != null && running > 0) return 'RUNNING';

  // If we can tell that all steps are finished, then classify terminal outcome.
  if (total != null && succeeded != null && failed != null && total > 0 && (succeeded + failed) >= total) {
    if (failed > 0) return 'FAILED';
    if (succeeded >= total) return 'SUCCEEDED';
  }

  // Non-terminal: if there are steps waiting/queued, keep non-failed status.
  if (ready != null && ready > 0) return 'READY';
  if (pending != null && pending > 0) return 'PENDING';
  if (total != null && succeeded != null && total > 0 && succeeded >= total) return 'SUCCEEDED';

  const steps: any[] = Array.isArray(snapshot?.steps) ? snapshot.steps : [];
  if (steps.length) {
    const st = steps.map((s) => normalizeRunStatus(s?.status));
    if (st.some((x) => isRunningStatus(x))) return 'RUNNING';
    // Only call FAILED if every step is terminal and at least one failed.
    const hasFailure = st.some((x) => x === 'FAILED' || x === 'ERROR');
    const allTerminal = st.every((x) => x === 'SUCCEEDED' || x === 'DONE' || x === 'FAILED' || x === 'ERROR');
    if (hasFailure && allTerminal) return 'FAILED';
    if (st.every((x) => x === 'SUCCEEDED' || x === 'DONE')) return 'SUCCEEDED';
    if (st.some((x) => x === 'READY')) return 'READY';
    if (st.some((x) => x === 'PENDING')) return 'PENDING';
  }

  return null;
}

function RunSummaryRich({
  runId,
  snapshot,
  loading,
  error,
}: {
  runId: string | null | undefined;
  snapshot: any;
  loading?: boolean;
  error?: any;
}) {
  // Keep relative timestamps (e.g. "12s ago") moving.
  useNow(1000);

  if (!runId) {
    return <div className="text-sm text-neutral-500">Run a workflow to see live status.</div>;
  }

  if (error) {
    return (
      <div className="space-y-2">
        <div className="text-sm text-rose-400">{String(error?.message || error)}</div>
        {!snapshot && loading ? <div className="text-sm text-neutral-500">Connecting…</div> : null}
      </div>
    );
  }

  if (!snapshot) {
    return <div className="text-sm text-neutral-500">{loading ? 'Connecting…' : 'Waiting for updates…'}</div>;
  }

  const effectiveStatus = deriveOverallStatusFromSnapshot(snapshot) ?? snapshot?.status;
  const statusLabel = toStatusLabel(effectiveStatus, 'PENDING');
  const isRunning = isRunningStatus(statusLabel);
  const percent = typeof snapshot?.percent === 'number' ? snapshot.percent : null;

  const counts = snapshot?.counts || {};
  const total = typeof counts.total === 'number' ? counts.total : null;
  const ok = typeof counts.succeeded === 'number' ? counts.succeeded : null;
  const failed = typeof counts.failed === 'number' ? counts.failed : null;
  const running = typeof counts.running === 'number' ? counts.running : null;
  const ready = typeof counts.ready === 'number' ? counts.ready : null;
  const pending = typeof counts.pending === 'number' ? counts.pending : null;

  const summary = snapshot?.summary ?? {};
  const run = snapshot?.run ?? {};

  // Go JSON encoding (no tags) yields DurationRunTotalMs, not duration_run_total_ms.
  const totalMs =
    coerceNumber(summary?.duration_run_total_ms)
    ?? coerceNumber(summary?.DurationRunTotalMs)
    ?? coerceNumber(summary?.duration_ms)
    ?? coerceNumber(summary?.DurationMs)
    ?? coerceNumber(run?.duration_run_total_ms)
    ?? coerceNumber(run?.DurationRunTotalMs)
    // fallback: compute from timestamps if present
    ?? (() => {
      const created = parseDateMs(run?.created_at ?? run?.CreatedAt);
      const updated = parseDateMs(run?.updated_at ?? run?.UpdatedAt);
      if (created == null || updated == null) return null;
      const diff = updated - created;
      return diff >= 0 ? diff : null;
    })()
    ?? null;

  const steps: any[] = Array.isArray(snapshot?.steps) ? snapshot.steps : [];
  const stepRows = steps
    .map((s) => {
      const stepId = String(s?.step_id ?? '').trim();
      const st = normalizeRunStatus(s?.status);
      const durationMs = extractDurationMs(s?.output);
      const attempts = typeof s?.attempts === 'number' ? s.attempts : null;
      return { stepId, st, durationMs, attempts };
    })
    .filter((x) => x.stepId)
    .sort((a, b) => a.stepId.localeCompare(b.stepId))
    .slice(0, 12);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className={
              'text-xs font-semibold '
              + (isRunning ? vumeterTextClass.trim() : getStatusTextClass(statusLabel))
            }
          >
            {statusLabel}
          </span>
          {Number.isFinite(percent as any) ? <div className="text-xs text-neutral-400">{percent}%</div> : null}
        </div>

        {snapshot?.polled_at ? (
          <div
            className="text-xs text-neutral-600 whitespace-nowrap"
            title={String(snapshot.polled_at)}
          >
            Last update: {timeAgo(snapshot.polled_at)}
            {snapshot?.unchanged ? <span className="text-neutral-700"> (no change)</span> : null}
          </div>
        ) : null}
      </div>

      <div className="text-xs text-neutral-500 flex flex-wrap items-center gap-x-3 gap-y-1">
        {total != null ? <span>Total: <span className="text-neutral-200">{total}</span></span> : null}
        {ok != null ? <span>OK: <span className="text-emerald-300">{ok}</span></span> : null}
        {failed != null ? <span>Failed: <span className="text-rose-300">{failed}</span></span> : null}
        {running != null ? <span>Running: <span className="text-amber-300">{running}</span></span> : null}
        {ready != null ? <span>Ready: <span className="text-sky-300">{ready}</span></span> : null}
        {pending != null ? <span>Pending: <span className="text-neutral-300">{pending}</span></span> : null}
        {totalMs != null ? <span>Workflow time: <span className="text-neutral-200">{formatMsCompact(totalMs)}</span></span> : null}
      </div>

      {stepRows.length ? (
        <div>
          <div className="text-[10px] text-neutral-500 mb-1">Step timing</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1">
            {stepRows.map((s) => {
              const stepStatusLabel = toStatusLabel(s.st, 'PENDING');
              const stepIsRunning = isRunningStatus(stepStatusLabel);
              return (
                <div key={s.stepId} className="flex items-center justify-between gap-3 text-xs">
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className={
                        'shrink-0 text-[10px] font-semibold '
                        + (stepIsRunning ? vumeterTextClass.trim() : getStatusTextClass(stepStatusLabel))
                      }
                    >
                      {stepStatusLabel}
                    </span>
                    <div className="text-neutral-200 truncate">{s.stepId}</div>
                    {s.attempts != null ? <div className="text-neutral-500">(attempts: {s.attempts})</div> : null}
                  </div>
                  <div className="text-neutral-400 whitespace-nowrap">{s.durationMs != null ? formatMsCompact(s.durationMs) : '—'}</div>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function keyOf(w: WorkflowCatalogEntry) {
  return `${w.workflow_key}::${w.version}`;
}

function inferEdgesFromBindings(nodes: Array<{ id: string; paramsBindingsJson: string }>, existingEdges: Array<{ from: string; to: string }>) {
  const rx = /^\{\{\s*steps\.([a-zA-Z0-9_-]+)\.outputs\.([a-zA-Z0-9_-]+)\s*\}\}$/;
  const existing = new Set(existingEdges.map((e) => `${e.from}->${e.to}`));
  const nodeIds = new Set(nodes.map((n) => n.id));
  const inferred: Array<{ id: string; from: string; to: string }> = [];

  const walk = (v: any, cb: (s: string) => void): void => {
    if (typeof v === 'string') {
      cb(v);
      return;
    }
    if (!v) return;
    if (Array.isArray(v)) {
      v.forEach((x) => walk(x, cb));
      return;
    }
    if (typeof v === 'object') {
      Object.values(v).forEach((x) => walk(x, cb));
    }
  };

  for (const n of nodes) {
    let obj: any = {};
    try {
      obj = JSON.parse(n.paramsBindingsJson || '{}');
    } catch {
      obj = {};
    }

    const deps = new Set<string>();
    walk(obj, (s) => {
      const m = String(s).trim().match(rx);
      if (!m) return;
      const parentId = m[1];
      if (!parentId || parentId === n.id) return;
      if (!nodeIds.has(parentId)) return;
      deps.add(parentId);
    });

    Array.from(deps)
      .sort()
      .forEach((from) => {
        const key = `${from}->${n.id}`;
        if (existing.has(key)) return;
        existing.add(key);
        inferred.push({ id: `inf_${from}_${n.id}`, from, to: n.id });
      });
  }

  return inferred;
}

function formatDateShort(value: string) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString();
  } catch {
    return value;
  }
}
